import type { ProjectId } from "features/shared/model/types";
import type { QueryClient } from "react-query";

import { useGeneralPaginatedAllPagesFetchHook } from "api/hooks/allHooks";
import { customApiCall } from "utils/axios";
import { getUserFromLocalStorage } from "utils/localStorage";

import type { IfcPipelineJob, RunExecutionParams } from "../model/types";

import { EXECUTION_CACHE_KEYS } from "./cacheKeys";
import { usePollWhileJobsInProgress } from "./hooks/usePollWhileJobsInProgress";
import executionUrls from "./urls";

export const runExecution = async ({
  executeRaytracing,
  file,
  meshingMethod,
  onUploadProgress,
  projectId,
  sensorOffsetF,
  signal,
}: RunExecutionParams) => {
  const user = getUserFromLocalStorage();
  const refreshToken = typeof user.refresh_token === "string" ? user.refresh_token : undefined;

  const data = {
    execute_raytracing: executeRaytracing,
    file,
    meshing_method: meshingMethod,
    project_id: projectId,
    refresh_token: refreshToken,
    sensor_offset_f: sensorOffsetF,
  };

  return customApiCall.upload({ data, onUploadProgress, signal, url: executionUrls.upload() });
};

export const useGetExecutionHistory = (projectId: ProjectId, enabled = false) => {
  const url = executionUrls.ifcJobsByProject(projectId!);
  const query = useGeneralPaginatedAllPagesFetchHook<IfcPipelineJob>({
    cacheName: EXECUTION_CACHE_KEYS.IFC_JOBS(projectId),
    rest: { enabled },
    url,
  });

  usePollWhileJobsInProgress(query);

  return query;
};

export const invalidateExecutionQueries = (queryClient: QueryClient, projectId: ProjectId) => {
  return Promise.all([
    queryClient.invalidateQueries(EXECUTION_CACHE_KEYS.IFC_JOBS(projectId)),
    queryClient.invalidateQueries(EXECUTION_CACHE_KEYS.PROCESSING_JOBS(projectId)),
  ]);
};
